import {
  IconCheck,
  IconChevronDown,
  IconClock,
  IconFileText,
  IconPlayerPause,
  IconPointFilled,
  IconSearch,
} from "@tabler/icons-react";
import { cn } from "../../design-system/components";
import { MessengerFilters, type MessengerFiltersValue } from "./MessengerFilters";
import "../messenger.css";
import { VyborLiniy } from "./VyborLiniy";
import { VyborProekta } from "./VyborProekta";
import { useEffect, useRef, useState } from "react";
import { useLabel, useText } from "../../clients/words";
import type {
  FilialPlashka,
  OcheredOtvet,
  Srochnost,
  StrokaOcheredi,
  Vkladka,
} from "../api";
import { ZnachokDostavki } from "./ZnachokDostavki";
import { ZnachokKanala } from "./ZnachokKanala";
import { NapisatNovoy } from "./NapisatNovoy";
import { usePermissions } from "../../auth/usePermissions";
import { IconMessage2Plus, IconX } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { razobrano } from "../api";
import { BAR_KEY, OCHERED_KEY, razborKey } from "../hooks";

/**
 * СПИСОК ОЧЕРЕДИ — «как в Telegram, привычно» (направление владельца 15.09).
 *
 * Строка в две линии: круг слева, имя и время ожидания на первой, ТОЛЬКО текст
 * последнего сообщения на второй, синий кружок непрочитанных справа.
 *
 * ⛔ ВТОРАЯ ЛИНИЯ — БЕЗ ЛИШНЕГО (владелец, дословно: «предпоказ фразы в списке
 * чатов — без лишнего в строке, т.е. иконки филиала и мессенджера из неё
 * убрать»). Канал и филиал уже сказаны кругом; повтори их в строке — и для
 * самой фразы места не останется.
 */

const VKLADKI: { value: Vkladka; label: string }[] = [
  // ⛔ «Активные» — ПЕРВАЯ и по умолчанию (владелец, п.11).
  { value: "aktivnye", label: "obrashcheniya.vkladka_aktivnye" },
  { value: "novye", label: "obrashcheniya.vkladka_novye" },
  { value: "v_rabote", label: "obrashcheniya.vkladka_v_rabote" },
  { value: "bez_klientki", label: "obrashcheniya.vkladka_bez_klientki" },
  { value: "razobrannye", label: "obrashcheniya.vkladka_razobrannye" },
];

/**
 * ⛔ ЭКРАН ОБЯЗАН СКАЗАТЬ, ПОЧЕМУ В КАНАЛЕ ТИХО (владелец, 15.09, п.14:
 * «написал в вацап — ничего не появилось вообще, а он подключен»).
 *
 * Это не ошибка приёма: входящие этого номера пока идут в amoCRM. Ошибка была
 * в том, что экран об этом МОЛЧАЛ — и «канал не подключён», «канал сломался» и
 * «канал ещё не переключён» выглядели одинаково.
 */
function PodskazkaAmo({ kanaly }: { kanaly: { kind_display: string }[] }) {
  const text = useText();

  if (kanaly.length === 0) return null;

  return (
    <div className="mx-1 mb-1.5 rounded-ops-card bg-ops-warning-soft px-2.5 py-1 text-ops-micro text-ops-warning">
      {kanaly.map((k) => k.kind_display).join(" · ")} —{" "}
      {text("obrashcheniya.vhodyashchie_cherez_amo")}
    </div>
  );
}

/** Строка выпадающего выбора: цвет филиала точкой, выбранное — галочкой. */
function VyborStroka({
  vybrana,
  color,
  onClick,
  children,
}: {
  vybrana: boolean;
  color?: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 px-3 py-2 text-left text-ops-caption text-ops-text hover:bg-ops-surface-2"
    >
      {color !== undefined && (
        <span
          className="h-2 w-2 shrink-0 rounded-ops-pill"
          style={{ background: color }}
          aria-hidden
        />
      )}
      <span className="flex-1 truncate">{children}</span>
      {vybrana && <IconCheck size={15} className="shrink-0 text-ops-primary" />}
    </button>
  );
}

/** Цвет ожидания — по порогам сервера, а не по числу, придуманному здесь. */
const CVET_SROCHNOSTI: Record<Srochnost, string> = {
  net: "text-ops-text-tertiary",
  obychno: "text-ops-text-tertiary",
  preduprezhdenie: "text-ops-warning",
  opasno: "text-ops-danger",
};

interface Props {
  dannye: OcheredOtvet | undefined;
  gruzitsya: boolean;
  vybran: number | null;
  poisk: string;
  onPoisk: (v: string) => void;
  onVkladka: (v: Vkladka) => void;
  /**
   * Свой выбор филиала — ТОЛЬКО там, где нет шапки панели (лайт-чат). В панели
   * филиал выбирается в шапке, и второго переключателя у очереди нет
   * (владелец, 24.09.2026, `s635` Д2; op-rules §31.8).
   */
  onFilial?: (id: number | null) => void;
  onOtkryt: (id: number) => void;
  /** `s635` (ТЗ v3 §4.13): фильтр источников «форма · звонок». */
  onIstochnik?: (kod: string | null) => void;
  /** `[s635-filtr-linii]`: линии — одна или несколько. */
  onLinii?: (ids: number[]) => void;
  messengerLinii?: number[];
  /** `s675`: «Все проекты ▾» — рядом с линиями. */
  onProekty?: (ids: number[]) => void;
  messengerFiltry?: MessengerFiltersValue;
  onMessengerFiltry?: (v: MessengerFiltersValue) => void;
  onReset?: () => void;
  filtered?: boolean;
  estEsche?: boolean;
  gruzitEsche?: boolean;
  onEsche?: () => void;
}

export function Ochered({
  dannye,
  gruzitsya,
  vybran,
  poisk,
  onPoisk,
  onVkladka,
  onFilial,
  onOtkryt,
  onIstochnik,
  onLinii,
  messengerLinii,
  onProekty,
  messengerFiltry, onMessengerFiltry, onReset, filtered, estEsche, gruzitEsche, onEsche,
}: Props) {
  const label = useLabel();
  const text = useText();
  const { can } = usePermissions();
  const [pishemNovoy, setPishemNovoy] = useState(false);
  /*
    `s731` (владелец 07.10.2026): крестик «Закрыть переписку» в плашке списка. Ход — тот же, что у
    кнопки «Закрыть» в самом диалоге (`razobrano`, исход «ответили»), с теми же правами: дверь одна.
    После удачи список, счётчики и раздел закрытых перечитываются сами; открытый справа диалог
    получает свежее состояние из ответа хода. Отказ сервера остаётся в окне своими словами, а
    переписка — на месте.
  */
  const qc = useQueryClient();
  const [zakryvaem, setZakryvaem] = useState<StrokaOcheredi | null>(null);
  const [zakrytieIdyot, setZakrytieIdyot] = useState(false);
  const [zakrytieOtkaz, setZakrytieOtkaz] = useState<string | null>(null);
  const zakrytPerepisku = async () => {
    if (zakryvaem === null || zakrytieIdyot) return;
    setZakrytieIdyot(true);
    setZakrytieOtkaz(null);
    try {
      const svezhiy = await razobrano(zakryvaem.id, "answered");
      qc.setQueryData(razborKey(zakryvaem.id), svezhiy);
      void qc.invalidateQueries({ queryKey: OCHERED_KEY });
      void qc.invalidateQueries({ queryKey: BAR_KEY });
      setZakryvaem(null);
    } catch (e: unknown) {
      const pochemu = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? null;
      setZakrytieOtkaz(text("messenger.list.close_failed", { почему: pochemu ?? "нет ответа сервера, попробуйте ещё раз" }));
    } finally {
      setZakrytieIdyot(false);
    }
  };
  /*
    ⛔ ШАПКА «ПЕРЕПИСОК» — КОМПАКТНАЯ (владелец, 04.10.2026, снимок с красной
    рамкой: «очень большой, нужно сделать компактно, в высоте максимум 35–40 %
    от текущего»). Замер до: 300 точек от верха колонки до первой строки при
    ширине колонки 338. Кнопка «Написать новой» встаёт в строку поиска, линии и
    проекты — одной строкой внутри `MessengerFilters`. Лайт (`LaytEkran`) этих
    фильтров не передаёт и остаётся прежним: его кнопки красятся по структуре.
  */
  const kompakt = messengerFiltry !== undefined && onMessengerFiltry !== undefined && onReset !== undefined;
  const knopkaNovoy = can("message.send") && (
    <button
      type="button"
      onClick={() => setPishemNovoy(true)}
      className={kompakt
        ? "flex h-9 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-ops-pill bg-ops-primary px-2.5 text-ops-caption font-medium text-ops-on-primary sm:h-[28px]"
        : "mx-1 mb-2 flex items-center justify-center gap-2 rounded-ops-pill bg-ops-primary px-4 py-2 text-ops-body font-medium text-ops-on-primary"}
    >
      <IconMessage2Plus size={kompakt ? 16 : 18} />
      {text("obrashcheniya.novaya.knopka")}
    </button>
  );
  const vybory = (
    <>
      {/* `[s635-filtr-linii]`: «Все линии ▾» рядом с источниками. */}
      {onLinii !== undefined && (
        <VyborLiniy messenger={messengerFiltry !== undefined} linii={dannye?.linii ?? []} vybrany={messengerLinii ?? dannye?.vybrany_linii ?? []} onVybor={onLinii} className={kompakt ? undefined : "ml-1"} />
      )}
      {onProekty !== undefined && (
        <VyborProekta proekty={dannye?.proekty ?? []} vybrany={dannye?.vybrany_proekty ?? []} onVybor={onProekty} className={kompakt ? undefined : "ml-1"} />
      )}
    </>
  );
  const pilyuliIstochnikov = onIstochnik !== undefined && (dannye?.istochniki ?? []).length > 0;

  return (
    <div className="flex min-h-0 flex-col">
      {/*
        ⛔ «НАПИСАТЬ НОВОЙ» (владелец, 24.09.2026, картинка `state/nk`). Кнопка
        стоит здесь, в общем списке, — поэтому она одна и в панели, и в лайте.
        Своей строкой, а не в строке поиска и не в ряду вкладок: лайт красит
        их кнопки селекторами по структуре (`layt.css`), и здесь она стала бы
        «Все филиалы» или пилюлей вкладки. Без права писать кнопки нет: завести
        разговор, из которого нельзя отправить шаблон, — тупик.
      */}
      {!kompakt && knopkaNovoy}
      {pishemNovoy && <NapisatNovoy onZakryt={() => setPishemNovoy(false)} onOtkryt={onOtkryt} />}
      {zakryvaem !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
          data-testid="zakryt-perepisku-okno"
          role="dialog"
          aria-modal="true"
          aria-label={text("messenger.list.close_question")}
          onClick={(e) => { if (e.target === e.currentTarget && !zakrytieIdyot) setZakryvaem(null); }}
          onKeyDown={(e) => { if (e.key === "Escape" && !zakrytieIdyot) setZakryvaem(null); }}
        >
          <div className="w-full max-w-sm rounded-ops-card bg-ops-surface p-4 shadow-lg">
            <p className="mb-1 text-ops-label font-medium text-ops-text">{text("messenger.list.close_question")}</p>
            <p className="mb-3 truncate text-ops-caption text-ops-text-secondary">
              {zakryvaem.customer_display ?? text("obrashcheniya.kto_eto")}
            </p>
            {zakrytieOtkaz !== null && (
              <p className="mb-3 rounded-ops-md bg-ops-danger-soft px-3 py-2 text-ops-caption text-ops-danger" role="alert" data-testid="zakryt-perepisku-otkaz">
                {zakrytieOtkaz}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                autoFocus
                onClick={() => setZakryvaem(null)}
                disabled={zakrytieIdyot}
                data-testid="zakryt-perepisku-otmena"
                className="min-h-10 rounded-ops-md border border-ops-border px-4 py-2 text-ops-caption text-ops-text disabled:opacity-40"
              >
                {text("messenger.list.close_cancel")}
              </button>
              <button
                type="button"
                onClick={() => void zakrytPerepisku()}
                disabled={zakrytieIdyot}
                data-testid="zakryt-perepisku-da"
                className="min-h-10 rounded-ops-md bg-ops-primary px-4 py-2 text-ops-caption font-medium text-ops-on-primary disabled:opacity-40"
              >
                {text("messenger.actions.close")}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ⛔ ЗАГОЛОВКА НА ЭКРАНЕ НЕТ (владелец, 15.09, п.2: «слово обращения
          вообще убрать»). В меню он остаётся — там он и отвечает на вопрос «где
          я»; на самом экране это строка, съедающая на телефоне высоту одной
          строки очереди и не говорящая ничего нового. Ключ `obrashcheniya.zagolovok`
          из словаря НЕ сносится: им подписан пункт меню и вкладка браузера. */}
      {/*
        ⛔ ВЫСОТА НАД СПИСКОМ — СЧИТАННАЯ ВЕЛИЧИНА, А НЕ «НУ КАК-ТО» (`s605`,
        замер на 390 по скринам владельца: до первой строки очереди уходило
        **259 точек из 844** — почти треть экрана на поиск и два ряда фильтров.
        В мессенджере, с которым владелец сравнивает, до первой беседы ~110.
        Отступы ужаты на телефоне и остаются прежними на столе, где места вдоволь.
      */}
      {/*
        ⛔ ПОИСК И ФИЛИАЛ — ОДНА СТРОКА (макет Астры, 16.09). Раньше это были
        две строки: поиск во всю ширину и под ним ряд плашек филиалов, который
        на 390 уходил за край. Теперь одна строка на обе величины.
      */}
      <div className={kompakt ? "flex items-center gap-1 px-1 pb-1" : "flex items-center gap-1 px-1 pb-1 sm:pb-2"}>
        <div className="relative min-w-0 flex-1">
          <IconSearch
            size={16}
            className={cn("pointer-events-none absolute top-1/2 -translate-y-1/2 text-ops-text-tertiary", kompakt ? "left-2.5" : "left-3.5")}
          />
          <input
            type="search"
            value={poisk}
            onChange={(e) => onPoisk(e.target.value)}
            placeholder={label("obr_poisk")}
            className={cn(
              "w-full rounded-ops-pill border border-ops-border bg-ops-surface text-ops-caption text-ops-text outline-none focus:border-ops-primary",
              kompakt ? "h-9 text-ellipsis py-0 pl-8 pr-2 placeholder:text-ellipsis sm:h-[28px]" : "py-1.5 pl-10 pr-4 sm:py-2",
            )}
          />
        </div>

        {kompakt && knopkaNovoy}

        {onFilial !== undefined && (
          <VyborFiliala
            filialy={dannye?.filialy ?? []}
            vybran={dannye?.filial_id ?? null}
            onVybor={onFilial}
          />
        )}
      </div>

      <PodskazkaAmo kanaly={dannye?.kanaly_cherez_amo ?? []} />

      {/* `s635` (ТЗ v3 §4.13): источник входа — одна строка пилюль, считает сервер. */}
      {/*
        ⛔ `s681` (поправка штаба 28.09): фильтров «форма» / «звонок» в «Переписках» нет —
        сервер их не отдаёт. Ряд живёт ради линий и проектов; пилюли источников —
        только если сервер их назвал (иначе «Все» стояло бы одно и ничего не значило).
      */}
      {(kompakt ? pilyuliIstochnikov : onIstochnik !== undefined || onLinii !== undefined || onProekty !== undefined) && (
        <div className="flex flex-wrap items-center gap-1 px-1 pb-1">
          {onIstochnik !== undefined && pilyuliIstochnikov && [{ kod: null as string | null, slovo: text("obrashcheniya.istochnik.vse") }, ...(dannye?.istochniki ?? [])].map((i) => (
            <button
              key={i.kod ?? "vse"}
              type="button"
              onClick={() => onIstochnik(i.kod)}
              className={
                (dannye?.istochnik ?? null) === i.kod
                  ? "rounded-ops-pill bg-ops-primary px-3 py-1 text-ops-caption text-ops-on-primary"
                  : "rounded-ops-pill border border-ops-border px-3 py-1 text-ops-caption text-ops-text-secondary hover:bg-ops-surface-2"
              }
            >
              {i.slovo}
            </button>
          ))}
          {!kompakt && vybory}
        </div>
      )}

      {kompakt ? <MessengerFilters value={messengerFiltry} data={dannye} onChange={onMessengerFiltry} onReset={onReset} filtered={filtered ?? false} vybory={onLinii !== undefined || onProekty !== undefined ? vybory : undefined} /> : <RyadVkladok
        vkladka={dannye?.vkladka ?? "aktivnye"}
        schyotchiki={dannye?.schyotchiki}
        troyka={dannye?.troyka_vkladok}
        onVkladka={onVkladka}
      />}

      <div className="mt-1 min-h-0 flex-1 overflow-y-auto rounded-ops-card border border-ops-border bg-ops-surface sm:mt-2">
        {gruzitsya && dannye === undefined ? (
          <SkeletStrok />
        ) : (dannye?.stroki.length ?? 0) === 0 ? (
          messengerFiltry && filtered ? <div className="px-4 py-10 text-center text-ops-body">{text('messenger.filters.empty')}</div> : <Pusto
            proverenoV={dannye?.provereno_v ?? null}
            zhdutVsego={dannye?.zhdut_vsego ?? 0}
            zhdutDisplay={dannye?.zhdut_display ?? null}
            onTuda={() => onVkladka(dannye?.zhdut_vkladka ?? "aktivnye")}
            etoTaVkladka={dannye?.zhdut_vkladka === dannye?.vkladka}
            poisk={dannye?.poisk ?? null}
          />
        ) : (
          dannye?.stroki.map((s, i) => (
            <Stroka
              key={s.id}
              stroka={s}
              posledniy={i === dannye.stroki.length - 1}
              vybran={s.id === vybran}
              onOtkryt={() => onOtkryt(s.id)}
              onZakryt={s.mozhno_zakryt === true && can("inquiry.queue.view") ? () => { setZakrytieOtkaz(null); setZakryvaem(s); } : undefined}
            />
          ))
        )}
        {estEsche && onEsche && <button type="button" className="messenger-more" disabled={gruzitEsche} onClick={onEsche}>{text(gruzitEsche ? 'messenger.filters.loading' : 'messenger.filters.more')}</button>}
      </div>
    </div>
  );
}

function VyborFiliala({
  filialy,
  vybran,
  onVybor,
}: {
  filialy: FilialPlashka[];
  vybran: number | null;
  onVybor: (id: number | null) => void;
}) {
  const text = useText();
  const [otkryt, setOtkryt] = useState(false);
  const korob = useRef<HTMLDivElement | null>(null);

  /*
   * ⛔⛔ ФИЛИАЛ — ВЫПАДАЮЩИЙ ВЫБОР, А НЕ РЯД ПЛАШЕК (`s605`, решение Астры по
   * жалобе владельца «все расползлось или не влезо»).
   *
   * Ряд плашек был честной попыткой выполнить прежнюю просьбу — «все филиалы,
   * лэш и пр — уместить в одну строку». Уместили: строка одна, но на 390 она
   * уходит за край, и «Beauty Medical» обрезано. Замер: 15 точек за краем у
   * филиалов и 279 у вкладок — четыре ряда над списком съедали треть экрана.
   *
   * Выбор занимает одну кнопку вместо ряда и всегда показывает ВЫБРАННОЕ —
   * то есть отвечает на вопрос «что я сейчас вижу» лучше, чем ряд, где
   * выделенную плашку ещё надо найти глазами.
   */
  useEffect(() => {
    if (!otkryt) return;

    const zakryt = (e: MouseEvent) => {
      if (korob.current !== null && !korob.current.contains(e.target as Node))
        setOtkryt(false);
    };

    document.addEventListener("mousedown", zakryt);

    return () => document.removeEventListener("mousedown", zakryt);
  }, [otkryt]);

  const vybrannyy =
    vybran === null ? null : filialy.find((f) => f.id === vybran);

  return (
    <div ref={korob} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOtkryt((b) => !b)}
        className="flex max-w-[170px] items-center gap-1 rounded-ops-pill px-2 py-1 text-ops-caption text-ops-text-secondary hover:bg-ops-surface-2"
      >
        <span className="truncate">
          {vybrannyy?.short_name ?? text("obrashcheniya.vse_filialy")}
        </span>
        <IconChevronDown size={14} className="shrink-0" />
      </button>

      {otkryt && (
        <div className="absolute right-0 top-full z-30 mt-1 min-w-[190px] overflow-hidden rounded-ops-card border border-ops-border bg-ops-surface shadow-ops-drop">
          <VyborStroka
            vybrana={vybran === null}
            onClick={() => {
              onVybor(null);
              setOtkryt(false);
            }}
          >
            {text("obrashcheniya.vse_filialy")}
          </VyborStroka>

          {filialy.map((f) => (
            <VyborStroka
              key={f.id ?? "set"}
              vybrana={vybran === f.id}
              color={f.color}
              onClick={() => {
                onVybor(f.id);
                setOtkryt(false);
              }}
            >
              {f.name}
            </VyborStroka>
          ))}
        </div>
      )}
    </div>
  );
}

function RyadVkladok({
  vkladka,
  schyotchiki,
  troyka,
  onVkladka,
}: {
  vkladka: Vkladka;
  schyotchiki: Record<Vkladka, number> | undefined;
  troyka: OcheredOtvet["troyka_vkladok"] | undefined;
  onVkladka: (v: Vkladka) => void;
}) {
  const text = useText();

  /*
    ⛔⛔ ВКЛАДКИ ПЕРЕНОСЯТСЯ, А НЕ ПРОКРУЧИВАЮТСЯ ВБОК (`s605`, поймано
        ГЛАЗОМ на живом экране 16.09 после выкатки).

        Пять вкладок просят 633 точки, на телефоне владельца их 356. Ряд с
        прокруткой вбок оставлял на экране «Без клиентки» обрезанным до «Б…» —
        и владелец назвал это ровно тем, чем оно выглядит: «все расползлось или
        не влезо». Прокрутку у ряда вкладок никто не ищет: человек видит
        обрезанное слово и считает экран сломанным.

        Перенос на вторую строку стоит 28 точек высоты и снимает вопрос
        целиком: видно все пять и все счётчики, на любой ширине. На компьютере
    они и так встают в одну строку — правило одно, поведение разное само.
  */
  return (
    <div className="flex flex-wrap gap-1 px-1 pb-0.5 sm:pb-1">
      {VKLADKI.map((v) => {
        const aktivna = v.value === vkladka;
        const schyot = schyotchiki?.[v.value];

        return (
          <button
            key={v.value}
            type="button"
            onClick={() => onVkladka(v.value)}
            className={cn(
              "shrink-0 whitespace-nowrap rounded-ops-pill border px-2.5 py-0.5 text-ops-micro leading-5 transition-colors duration-ops ease-ops",
              aktivna
                ? "border-ops-primary bg-ops-primary text-ops-on-primary"
                : "border-ops-border bg-ops-surface text-ops-text-secondary",
            )}
          >
            {text(v.label)}
            {/* ⛔ Ноль печатается тоже: «Новые» без числа читались бы как
                «счётчик не работает», а «Новые 0» — как «никто не ждёт». */}
            {typeof schyot === "number" && (
              <span className="ml-1 font-semibold">{schyot}</span>
            )}
            {/*
              ⛔⛔ ВКЛАДКА ПОКАЗЫВАЕТ РАЗБОР, А НЕ ОДНО ЧИСЛО (`s605`, п.6
              владельца). «Активные 4» не отвечает ни на что: четыре чего —
              забытых, взятых в работу, отложенных? Три точки отвечают, не
              открывая вкладку, и цветом повторяют те же три вида, что в баре
              и в строке. Ноль своей точки не рисует: точка с нулём — шум.

              ⛔ Числа берутся из ТОГО ЖЕ ответа, что и строки; фронт не
              складывает и не выводит их сам — иначе завёлся бы четвёртый счёт
              ровно там, где чиним разъехавшиеся три.
            */}
            {troyka?.[v.value] !== undefined && (
              <span className="ml-1 inline-flex items-center gap-0.5 align-middle">
                {troyka[v.value].ne_prochitano > 0 && (
                  <span
                    className={cn(
                      "h-1.5 w-1.5 rounded-ops-pill",
                      aktivna ? "bg-ops-on-primary" : "bg-ops-danger",
                    )}
                    aria-hidden
                  />
                )}
                {troyka[v.value].zhdyot_otveta > 0 && (
                  <span
                    className={cn(
                      "h-1.5 w-1.5 rounded-ops-pill",
                      aktivna ? "bg-ops-on-primary/70" : "bg-ops-warning",
                    )}
                    aria-hidden
                  />
                )}
                {troyka[v.value].otlozheno > 0 && (
                  <span
                    className={cn(
                      "h-1.5 w-1.5 rounded-ops-pill",
                      aktivna ? "bg-ops-on-primary/40" : "bg-ops-text-tertiary",
                    )}
                    aria-hidden
                  />
                )}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function Stroka({
  stroka,
  posledniy,
  vybran,
  onOtkryt,
  onZakryt,
}: {
  stroka: StrokaOcheredi;
  posledniy: boolean;
  vybran: boolean;
  onOtkryt: () => void;
  /** `s731`: закрыть переписку из списка; `undefined` — крестика нет (закрыта, лид или прежний сервер). */
  onZakryt?: () => void;
}) {
  const text = useText();

  /*
    ⛔ `s731`: КРЕСТИК — СОСЕД СТРОКИ, А НЕ ЕЁ РЕБЁНОК (владелец 07.10.2026: «крестик не должен
    одновременно открывать диалог»). Строка — кнопка; кнопка внутри кнопки — негодная разметка, и
    нажатие по крестику доезжало бы до строки. Крестик стоит поверх правого края на уровне времени,
    строка под него освобождает место; область нажатия — 36 точек при значке в 16.
  */
  return (
    <div className="relative" data-stroka-obertka>
    <button
      type="button"
      data-stroka-ocheredi
      data-obrashchenie={stroka.id}
      data-vid-ozhidaniya={stroka.vid_ozhidaniya}
      onClick={onOtkryt}
      className={cn(
        "flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors duration-ops ease-ops",
        onZakryt !== undefined && "pr-11",
        !posledniy && "border-b border-ops-border",
        vybran ? "bg-ops-primary-soft" : "hover:bg-ops-surface-2",
      )}
    >
      <ZnachokKanala
        kind={stroka.kanal.kind}
        color={stroka.filial.color}
        title={`${stroka.kanal.kind_display} · ${stroka.filial.name}`}
      />

      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1.5 truncate text-ops-label font-medium">
            {stroka.customer_display === null ? (
              <span className="text-ops-danger">
                {text("obrashcheniya.kto_eto")}
              </span>
            ) : (
              <span className="truncate text-ops-text">
                {stroka.customer_display}
              </span>
            )}
            {stroka.est_zayavka && (
              <IconFileText
                size={15}
                className="shrink-0 text-ops-info"
                aria-label={text("obrashcheniya.est_zayavka")}
              />
            )}
          </span>

          {/*
            ⛔⛔ СПРАВА ОТ ИМЕНИ — ТОЛЬКО ВРЕМЯ (`s605`, поймано ГЛАЗОМ на живом
            экране сразу после выкатки).
            
            Предыдущая правка поставила сюда значок, слово вида и время одной
            строкой — и блок вытеснил ИМЯ: на 390 оно ужалось до «Бе…» и «Без
            име…». Имя — главная величина строки, по нему её узнают; время —
            служебная. Главное не уступает служебному место.
            
            В мессенджере, с которым владелец сравнивает, справа от имени стоит
            ровно одно короткое время. Так и здесь.
          */}
          <SkolkoZhdyot stroka={stroka} />
        </span>

        {/*
          ⛔ ВТОРАЯ ЛИНИЯ — ВИД ОЖИДАНИЯ И ФРАЗА. Ни филиала, ни мессенджера.
          
          Вид переехал сюда со своей строки: владелец просил оставить и значок,
          и слово («посмотрим ка на боевом будет»), но у имени для них места
          нет. Здесь оно есть — и вид стоит первым, до превью, потому что
          отвечает на более срочный вопрос, чем «что написали».
        */}
        <span className="mt-0.5 flex items-center gap-1 text-ops-caption text-ops-text-secondary">
          <VidOzhidaniyaStroki stroka={stroka} />
          {/*
            ⛔ ТОТ ЖЕ ЗНАЧОК, ЧТО В ЛЕНТЕ (`s608`). Здесь стояли две галочки по
            ЛЮБОМУ непустому статусу — то есть строка списка сообщала о
            прочтении там, где его никто не подтверждал, и расходилась с
            лентой того же диалога.
          */}
          {stroka.preview_nashe && (
            <ZnachokDostavki vid={stroka.preview_dostavka} razmer={14} />
          )}
          <span className="truncate">{stroka.preview}</span>
        </span>
      </span>

      {stroka.neprochitannyh > 0 && (
        <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-ops-primary px-1 text-ops-micro font-semibold text-ops-on-primary">
          {stroka.neprochitannyh}
        </span>
      )}
    </button>
      {onZakryt !== undefined && (
        <button
          type="button"
          data-testid="zakryt-perepisku-iz-spiska"
          data-obrashchenie={stroka.id}
          aria-label={text("messenger.list.close")}
          title={text("messenger.list.close")}
          onClick={(e) => { e.stopPropagation(); onZakryt(); }}
          className="absolute right-1 top-0.5 flex h-9 w-9 items-center justify-center rounded-ops-md text-ops-text-tertiary transition-colors duration-ops ease-ops hover:bg-ops-surface-2 hover:text-ops-danger focus-visible:text-ops-danger"
        >
          <IconX size={16} />
        </button>
      )}
    </div>
  );
}

/**
 * Значок вида ожидания — тот же набор, что в баре шапки: часы у ждущего
 * ответа, точка у непрочитанного, пауза у отложенного.
 *
 * ⛔ Значок идёт РЯДОМ СО СЛОВОМ, а не вместо него (решение владельца
 * 16.09 по макету Астры). Один смысл, два способа показать — это не дубль по
 * §31.8: дубль это две ВЕЛИЧИНЫ, а здесь одна величина и её подпись.
 */
function ZnachokVida({ vid }: { vid: StrokaOcheredi["vid_ozhidaniya"] }) {
  if (vid === "ne_prochitano") return <IconPointFilled size={12} aria-hidden />;
  if (vid === "zhdyot_otveta") return <IconClock size={12} aria-hidden />;
  if (vid === "otlozheno") return <IconPlayerPause size={12} aria-hidden />;

  return null;
}

/**
 * Справа от имени — ОДНО время: срок ожидания, срок возврата или время
 * последнего сообщения (Telegram-стиль). Одна величина в одном месте
 * (§31.8) — срок ожидания уже время события, второй раз его не показываем.
 *
 * ⛔ «Держит …» сюда не входит — переехало во вторую строку (`VidOzhidaniyaStroki`),
 * когда слот времени занят самим временем последнего сообщения, а не сроком
 * ожидания (владелец, приёмка 17.09.2026, п.2).
 */
function SkolkoZhdyot({ stroka }: { stroka: StrokaOcheredi }) {
  if (stroka.vid_ozhidaniya === "otlozheno" && stroka.otlozheno_do !== null) {
    return (
      <span className="shrink-0 whitespace-nowrap text-ops-micro text-ops-text-tertiary">
        {stroka.otlozheno_do}
      </span>
    );
  }

  if (stroka.zhdyot_skolko !== null || stroka.zhdyot_display !== null) {
    return (
      <span
        className={cn(
          "shrink-0 whitespace-nowrap text-ops-micro",
          CVET_SROCHNOSTI[stroka.srochnost],
        )}
      >
        {stroka.zhdyot_skolko ?? stroka.zhdyot_display}
      </span>
    );
  }

  if (stroka.poslednee_soobshchenie_vremya !== null) {
    return (
      <span className="shrink-0 whitespace-nowrap text-ops-micro text-ops-text-tertiary">
        {stroka.poslednee_soobshchenie_vremya}
      </span>
    );
  }

  return null;
}

/**
 * Вид ожидания во второй линии: значок и слово (решение владельца 16.09 —
 * «оставь пока и значок и текст посмотрим ка на боевом будет»).
 *
 * ⛔ У «отложено» вместо слова стоит ПРИЧИНА: «ждём врача» отвечает на вопрос
 * лучше, чем «отложено», а час возврата уже виден справа.
 */
function VidOzhidaniyaStroki({ stroka }: { stroka: StrokaOcheredi }) {
  const text = useText();

  if (stroka.vid_ozhidaniya === "tishina") {
    // ⛔ «Держит …» живёт здесь, а не в слоте времени первой строки —
    // слот времени занят временем последнего сообщения (см. `SkolkoZhdyot`).
    if (stroka.derzhit === null) return null;

    return (
      <span className="flex shrink-0 items-center gap-1 whitespace-nowrap text-ops-text-tertiary">
        {text("obrashcheniya.derzhit", { имя: stroka.derzhit })}
      </span>
    );
  }

  const slovo =
    stroka.vid_ozhidaniya === "otlozheno"
      ? (stroka.otlozheno_prichina_display ?? stroka.vid_ozhidaniya_display)
      : stroka.vid_ozhidaniya_display;

  return (
    <span
      className={cn(
        "flex shrink-0 items-center gap-1 whitespace-nowrap",
        stroka.vid_ozhidaniya === "otlozheno"
          ? "text-ops-text-tertiary"
          : CVET_SROCHNOSTI[stroka.srochnost],
      )}
    >
      <ZnachokVida vid={stroka.vid_ozhidaniya} />
      {slovo}
      <span className="text-ops-text-tertiary">·</span>
    </span>
  );
}

/**
 * Пусто — словами и с временем проверки, а не белым местом.
 *
 * ⛔ «ВСЁ ОТВЕЧЕНО» ГОВОРИТСЯ, ТОЛЬКО ЕСЛИ ЭТО ПРАВДА ПО ВСЕЙ ОЧЕРЕДИ. Глаз на
 * живом экране поймал: вкладка «Новые» пуста, экран радостно сообщает «Всё
 * отвечено», а в «В работе» клиентка ждёт ответа два часа. Пустая ВКЛАДКА и
 * пустая ОЧЕРЕДЬ — разные новости, и одно слово на две стёрло бы ту самую
 * разницу, ради которой очередь и заводилась.
 */
function Pusto({
  proverenoV,
  zhdutVsego,
  zhdutDisplay,
  onTuda,
  etoTaVkladka,
  poisk,
}: {
  proverenoV: string | null;
  zhdutVsego: number;
  zhdutDisplay: string | null;
  onTuda: () => void;
  etoTaVkladka: boolean;
  poisk: string | null;
}) {
  const text = useText();
  const vsyoTiho = zhdutVsego === 0 || etoTaVkladka;

  /*
   * ⛔ ПУСТОТА ПОИСКА — НЕ ТА ЖЕ ПУСТОТА (владелец, 15.09, п.13: «поиск ввожу
   * имя юрий — ничего не происходит или я не вижу»). «Всё отвечено» на пустом
   * поиске было бы прямой ложью: очередь не пуста, просто в ней нет ЭТОГО.
   * Пустота обязана назвать, по чему не нашлось, — иначе «не нашлось» и «поиск
   * сломан» на глаз неотличимы, что владелец и написал.
   */
  if (poisk !== null && poisk !== "") {
    return (
      <div className="px-4 py-10 text-center">
        <p className="text-ops-body font-medium text-ops-text">
          {text("obrashcheniya.nichego_ne_naydeno", { запрос: poisk })}
        </p>
      </div>
    );
  }

  return (
    <div className="px-4 py-10 text-center">
      <p className="text-ops-body font-medium text-ops-text">
        {vsyoTiho
          ? text("obrashcheniya.vsyo_otvecheno")
          : text("obrashcheniya.zdes_pusto")}
      </p>
      {vsyoTiho ? (
        proverenoV !== null && (
          <p className="mt-1 text-ops-caption text-ops-text-tertiary">
            {text("obrashcheniya.provereno", { время: proverenoV })}
          </p>
        )
      ) : (
        <button
          type="button"
          onClick={onTuda}
          className="mt-2 text-ops-caption text-ops-danger underline underline-offset-2"
        >
          {text("obrashcheniya.a_zhdut_tam")} · {zhdutDisplay}
        </button>
      )}
    </div>
  );
}

/** Скелет строк, а не спиннер на весь экран: список обязан быть узнаваем сразу. */
function SkeletStrok() {
  return (
    <div>
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="flex items-center gap-3 border-b border-ops-border px-3 py-2.5 last:border-b-0"
        >
          <div className="h-11 w-11 shrink-0 animate-pulse rounded-full bg-ops-surface-2" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-1/3 animate-pulse rounded-ops-pill bg-ops-surface-2" />
            <div className="h-3 w-2/3 animate-pulse rounded-ops-pill bg-ops-surface-2" />
          </div>
        </div>
      ))}
    </div>
  );
}
